import React, { memo, useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Menu, MenuItem, StepButton, StepLabel, Tooltip, CircularProgress } from '@material-ui/core';
import { config } from '../../../constants';
import AuditContext from './AuditContext';
import { CONSTANTS } from '../../../constants/constants';
import { getDocuments } from '../../../actions';
import IconMap from './icons';
import RelaunchForm from './RelaunchForm';
import Delegate from './Delegate';

const useStyles = makeStyles(() => ({
  step: {
    flex: 'initial'
  },
  stepButton: {
    margin: -8,
    padding: 8
  },
  stepLabel: {
    width: 80
  },
  textLabel: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

const StepStatus = ({ step, flow, userId, role, email, phone, getAllDocuments, onRelaunch, applicationType, ...rest }) => {
  const [relaunching, setRelaunching] = useState(false);
  const [openForm, setOpenForm] = useState(false);

  const [openDelegate, setOpenDelegate] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const styles = useStyles();
  const { t } = useTranslation();
  const { setStep } = useContext(AuditContext);
  const { flowId } = flow;

  const isNotificationStep = step.type === 'notificationStep';
  const flowIsActive = flow.status === CONSTANTS.FLOW.STATUS.SEND_TO_SIGN;
  const stepIsErroredOrRejected = [
    CONSTANTS.STEP.STATUS.ERROR,
    CONSTANTS.STEP.STATUS.REJECTED,
    CONSTANTS.STEP.STATUS.EXPIRED
  ].includes(step.status);
  let signStepIsActive = false;

  const nextStep = flow.steps.find(s => s.stepId === step.nextStepId);
  if (nextStep && nextStep.status === CONSTANTS.STEP.STATUS.SEND_TO_SIGN) {
    signStepIsActive = true;
  }

  const previousStep = flow.steps.find(flowStep => flowStep.nextStepId === step.stepId);

  const stepIsSendToSign = step.status === CONSTANTS.STEP.STATUS.SEND_TO_SIGN;
  const stepisNotAutomatic = !step.automatic;
  const userIdIsStepUserId = step.userId === userId;

  const previousStepIsNotification = previousStep && previousStep.type === 'notificationStep';
  const previousStepIsSigned = previousStep && previousStep.status === CONSTANTS.STEP.STATUS.SIGNED;
  const previousStepIncludesEmail = previousStep && previousStep.emails && previousStep.emails.includes(email);
  const previousStepIncludesPhone = previousStep && previousStep.mobiles && previousStep.mobiles.includes(phone);
  const isUserIncludedInPreviousNotification = previousStepIsNotification && previousStepIsSigned &&
    (previousStepIncludesEmail || previousStepIncludesPhone);

  const isUserAllowedToSign = stepIsSendToSign && stepisNotAutomatic && (userIdIsStepUserId || isUserIncludedInPreviousNotification);

  const isUserAllowedToRelaunch = role !== CONSTANTS.ROLES.READER;

  const hasMenu =
    (flowIsActive && isUserAllowedToSign) || (isUserAllowedToRelaunch && (
      stepIsErroredOrRejected || (isNotificationStep && flowIsActive && (
        signStepIsActive || step.status === CONSTANTS.STEP.STATUS.INIT))
    ));

  const showDelegateButton =
    ![CONSTANTS.STEP.STATUS.ERROR, CONSTANTS.STEP.STATUS.EXPIRED].includes(step.status) &&
    step.type === 'notificationStep' &&
    isUserAllowedToRelaunch;

  const openAudit = () => setStep(step);

  const handleClick = (event) => {
    if (hasMenu) return setAnchorEl(event.currentTarget);
    else openAudit();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getIcon = () => {
    let title = t(`documentSignStatus:${step.status}`);

    if (step.status === CONSTANTS.STEP.STATUS.REJECTED) {
      title = step.rejectedReason;
    }

    if (step.status === CONSTANTS.STEP.STATUS.ERROR && flow.lastError) {
      title = JSON.stringify(flow.lastError);
    }

    const hasWarningFlag = step.flag === 'warning';

    let iconKey = step.status;
    if (hasWarningFlag) iconKey = 'warning';
    if (isUserAllowedToSign) iconKey = 'create';

    if (relaunching) return <CircularProgress size={24} />;

    return <Tooltip title={title}>{IconMap[iconKey]}</Tooltip>;
  };

  const openDelegateForm = useCallback(() => {
    setOpenDelegate(true);
    setAnchorEl(null);
  }, []);

  const openRelaunchForm = useCallback(() => {
    setOpenForm(true);
    setAnchorEl(null);
  }, []);

  const handleRelaunch = useCallback(async ({ updatedStep }) => {
    setRelaunching(true);
    await onRelaunch(updatedStep || step);
    await getAllDocuments();
    setRelaunching(false);
  }, [step]);

  const getAction = () => {
    if (isUserAllowedToSign) {
      return (
        <MenuItem
          component={Link}
          target='_blank'
          to={{ pathname: `${config.FIRMA_FRONT_URL}/sign/${flowId}/${step.stepId}?urlCallback=${encodeURIComponent(`${window.location.origin}/document`)}` }}
        >
          {t('documentSignStatus:sign')}
        </MenuItem>
      );
    }

    if (!isUserAllowedToRelaunch) {
      return null;
    }

    const label = step.status === CONSTANTS.STEP.STATUS.INIT
      ? t('documentSignStatus:modify')
      : isNotificationStep
        ? t('documentSignStatus:resend')
        : t('documentSignStatus:relaunch');

    if (step.stepId === flow.steps[flow.steps.length - 1].stepId || applicationType === 'SIGNER') {
      return null;
    }
    if (CONSTANTS.STEP.STATUS.EXPIRED === step.status) return null;
    return (
      <MenuItem
        onClick={isNotificationStep ? openRelaunchForm : handleRelaunch}
      >
        {label}
      </MenuItem>
    );
  };

  return (
    <>
      {openForm && (
        <RelaunchForm
          flow={flow}
          step={step}
          handleRelaunch={handleRelaunch}
          onClose={setOpenForm}
        />
      )}
      {openDelegate && (
        <Delegate
          flow={flow}
          step={step}
          handleRelaunch={handleRelaunch}
          onClose={setOpenDelegate}
        />
      )}
      <StepButton
        {...rest}
        icon={getIcon()}
        onClick={handleClick}
        className={styles.stepButton}
      >
        <StepLabel className={styles.stepLabel}>
          <div
            className={styles.textLabel}
            title={step.alias}
          >
            {step.alias || t(`processDetail:stepType:${step.type}`)}
          </div>
        </StepLabel>
      </StepButton>
      {hasMenu && (
        <Menu
          id='step-menu'
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {getAction()}
          <MenuItem onClick={openAudit}>{t('documentSignStatus:viewAudit')}</MenuItem>
          {showDelegateButton && (
            <MenuItem onClick={openDelegateForm}>{(step.stepId === flow.steps[flow.steps.length - 1].stepId) ? t('documentSignStatus:modify') : t('documentSignStatus:Delegate')}</MenuItem>
          )}
        </Menu>
      )}
    </>
  );
};

StepStatus.propTypes = {
  step: PropTypes.object.isRequired,
  flow: PropTypes.object.isRequired,
  applicationType: PropTypes.string,
  userId: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  getAllDocuments: PropTypes.func.isRequired,
  onRelaunch: PropTypes.func.isRequired,
  // The next props are injected by parent
  active: PropTypes.bool,
  completed: PropTypes.bool,
  disabled: PropTypes.bool,
  last: PropTypes.bool,
  alternativeLabel: PropTypes.bool,
  expanded: PropTypes.bool,
  orientation: PropTypes.string
};

const mapStateToProps = (state) => {
  return {
    role: state.config.role
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getAllDocuments: () => dispatch(getDocuments())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(StepStatus));
